import type { ProjectNeedsWithLegalSkills } from '@axiom/validation';
import { positionReservedTypeValues } from '@axiom/validation';

import { DirectEngagementFormDeadEndModalTypes } from './direct-engagements-form-dead-end-modal.types';
import { DirectEngagementsFormUtil } from './direct-engagements-form-util';

type ProjectNeedsWithLegalSkillsKeys = keyof ProjectNeedsWithLegalSkills;

export const DirectEngagementsFormDeadEndModalConfigs = (
  modalType: DirectEngagementFormDeadEndModalTypes,
  projectNeeds?: Partial<Pick<ProjectNeedsWithLegalSkills, 'companyLocation'>>
) => {
  const state =
    DirectEngagementsFormUtil.findState(projectNeeds?.companyLocation) || '';

  switch (modalType) {
    case DirectEngagementFormDeadEndModalTypes.LEGAL_SUPPORT_HOURS: {
      return {
        title: 'Consider this change to improve your matches',
        content:
          'Legal support roles require a minimum of 20 hours per week. To improve your instant matches, increase the hours to 20 or change the role to Lawyer.',
        radioOptions: [
          {
            value: 'Increase hours to 20',
            fieldName: 'billingUnitsPerWeek' as ProjectNeedsWithLegalSkillsKeys,
            fieldValue: 20,
          },
          {
            value: 'Change role to Lawyer',
            fieldName: 'occupationType' as ProjectNeedsWithLegalSkillsKeys,
            fieldValue: 'Lawyer',
          },
        ],
      } as const;
    }

    case DirectEngagementFormDeadEndModalTypes.MIN_PAY_STATES: {
      return {
        title: 'Consider this change to improve your matches',
        content: `Lawyers who work in ${state || 'this state'} must be paid a minimum amount per week. To improve your instant matches, increase weekly hours to 20 or change work location to remote.`,
        radioOptions: [
          {
            value: 'Increase hours to 20',
            fieldName: 'billingUnitsPerWeek' as ProjectNeedsWithLegalSkillsKeys,
            fieldValue: 20,
          },
          {
            value: 'Change work location to remote',
            fieldName: 'worksite' as ProjectNeedsWithLegalSkillsKeys,
            fieldValue: 'Remote',
          },
        ],
      } as const;
    }

    case DirectEngagementFormDeadEndModalTypes.PAY_AS_YOU_GO_BUDGET: {
      return {
        title: 'Consider this change to improve your matches',
        content:
          'The average “Pay as you go” hourly rate is $200. To improve your instant matches, increase the rate to $200 or change to “Reserved” pricing.',
        radioOptions: [
          {
            value: 'Increase rate to $200',
            fieldName: 'maxBudgetPerHour' as ProjectNeedsWithLegalSkillsKeys,
            fieldValue: 200,
          },
          {
            value: 'Change to Reserved pricing',
            fieldName: 'reservedType' as ProjectNeedsWithLegalSkillsKeys,
            fieldValue: positionReservedTypeValues.Reserved,
          },
        ],
      } as const;
    }

    case DirectEngagementFormDeadEndModalTypes.RESERVED_PRICING: {
      return {
        title: 'Consider this change to improve your matches',
        content:
          'Reserved pricing requires at least 10 hours per week. To improve your instant matches, increase weekly hours to 10 or choose “Pay as you go”.',
        radioOptions: [
          {
            value: 'Increase hours to 10',
            fieldName: 'billingUnitsPerWeek' as ProjectNeedsWithLegalSkillsKeys,
            fieldValue: 10,
          },
          {
            value: 'Choose “Pay as you go”',
            fieldName: 'reservedType' as ProjectNeedsWithLegalSkillsKeys,
            fieldValue: positionReservedTypeValues['Not Reserved'],
          },
        ],
      } as const;
    }

    default: {
      throw new Error(`Unknown modal type: ${modalType as string}`);
    }
  }
};
